import { useCallback, useEffect, useState } from "react";
import { Loader2, Phone, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatLongDate, todayISO } from "@/lib/format";
import StatusBadge from "@/components/admin/StatusBadge";
import { AdminButton, AdminCard, EmptyState, adminInput } from "@/components/admin/ui";
import ReservationDialog from "@/pages/admin/ReservationDialog";

const ACTIONS = [
  ["confirmed", "Conferma"],
  ["completed", "Arrivata"],
  ["no-show", "No-show"],
  ["cancelled", "Annulla"],
];

export function ReservationRow({ res, onUpdate, onEdit }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    await onUpdate(res.id, { status });
    setBusy(false);
  };
  return (
    <li className="border border-border bg-cream p-4" data-testid={`reservation-${res.reference}`}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <span className="font-serif text-2xl text-wood tabular-nums">{res.time}</span>
        <span className="text-sm text-foreground/60">
          {res.guest_count} {res.guest_count === 1 ? "persona" : "persone"} · {res.service_name}
        </span>
        <StatusBadge status={res.status} testId={`status-${res.reference}`} />
        <span className="ml-auto text-[0.62rem] tracking-widest text-foreground/40">{res.reference}</span>
      </div>
      <div className="mt-2.5">
        <p className="text-base text-wood">
          {res.first_name} {res.last_name}
        </p>
        <a
          href={`tel:${res.phone.replace(/\s/g, "")}`}
          className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-sage"
        >
          <Phone size={13} /> {res.phone}
        </a>
        <p className="text-xs text-foreground/45">{res.email}</p>
        {res.notes && (
          <p className="mt-2 border-l-2 border-gold pl-3 text-sm text-foreground/70">Note: {res.notes}</p>
        )}
        {res.source !== "online" && (
          <p className="mt-1 text-[0.62rem] uppercase tracking-[0.16em] text-foreground/40">
            Origine: {res.source}
            {res.overridden ? " · capienza forzata" : ""}
          </p>
        )}
      </div>
      <div className="mt-3.5 flex flex-wrap gap-2">
        {ACTIONS.filter(([s]) => s !== res.status).map(([status, label]) => (
          <AdminButton
            key={status}
            variant={status === "cancelled" ? "danger" : status === "confirmed" ? "solid" : "outline"}
            disabled={busy}
            onClick={() => setStatus(status)}
            data-testid={`action-${status}-${res.reference}`}
          >
            {label}
          </AdminButton>
        ))}
        <AdminButton variant="ghost" onClick={() => onEdit(res)} data-testid={`action-edit-${res.reference}`}>
          Modifica
        </AdminButton>
      </div>
    </li>
  );
}

export default function AdminToday() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/summary", { params: { date: d } });
      setData(res.data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/reservations/${id}`, patch);
      await load(date);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const totals = data?.totals;

  return (
    <div className="space-y-6" data-testid="admin-today">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Oggi</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">{formatLongDate(date)}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            data-testid="today-date"
            className={`${adminInput} w-auto`}
          />
          <AdminButton variant="wood" onClick={() => setDialog({ mode: "create", date })} data-testid="new-reservation">
            <Plus size={14} /> Nuova prenotazione
          </AdminButton>
        </div>
      </div>

      {error && (
        <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {loading && (
        <p className="flex items-center gap-2 text-sm text-foreground/60">
          <Loader2 size={15} className="animate-spin" /> Carichiamo…
        </p>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="today-stats">
            {[
              ["Coperti", totals.guests],
              ["Prenotazioni", totals.reservations],
              ["In attesa", totals.pending],
              ["Confermate", totals.confirmed],
              ["Annullate", totals.cancelled],
              ["No-show", totals.no_show],
            ].map(([label, value]) => (
              <div key={label} className="border border-border bg-cream px-4 py-4">
                <p className="text-[0.6rem] uppercase tracking-[0.16em] text-wood/50">{label}</p>
                <p className="mt-1.5 font-serif text-3xl text-wood tabular-nums">{value}</p>
              </div>
            ))}
          </div>

          {data.closed ? (
            <EmptyState
              title="Chiuso"
              description={data.special?.reason || "Nessun servizio previsto in questa data."}
              testId="today-closed"
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {data.services.map((svc) => (
                <div key={svc.id} className="border border-border bg-cream px-4 py-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-serif text-xl text-wood">{svc.name}</p>
                    <span className="text-xs text-foreground/50">
                      {svc.start_time} – {svc.end_time}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-foreground/70">
                    {svc.booked} / {svc.capacity} coperti · {svc.reservations} prenotazioni
                  </p>
                  <div className="mt-2 h-1 bg-beige">
                    <div
                      className="h-1 bg-sage"
                      style={{ width: `${Math.min(100, (svc.booked / Math.max(1, svc.capacity)) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <AdminCard title="Prenotazioni del giorno" testId="today-reservations">
            {data.reservations.length === 0 ? (
              <EmptyState title="Nessuna prenotazione" description="Per ora non c'è nulla in agenda." />
            ) : (
              <ul className="space-y-3">
                {data.reservations.map((res) => (
                  <ReservationRow
                    key={res.id}
                    res={res}
                    onUpdate={update}
                    onEdit={(r) => setDialog({ mode: "edit", reservation: r })}
                  />
                ))}
              </ul>
            )}
          </AdminCard>
        </>
      )}

      {dialog && (
        <ReservationDialog
          {...dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            load(date);
          }}
        />
      )}
    </div>
  );
}
